import { API_ENDPOINTS } from '../constants/api';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { TIMEOUT_CONFIG } from '../constants/common';
import { putBlobToPresignedUrl } from '../utils/s3DirectUpload';

class FileService {
  /**
   * Get content type based on file extension
   * @param {File} file - The file to get content type for
   * @returns {string} - The content type of the file
   */
  getContentType(file) {
    if (!file) return 'application/octet-stream';
    
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    switch (fileExtension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'heic':
        return 'image/heic';
      case 'heif':
        return 'image/heif';
      case 'gif':
        return 'image/gif';
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      case 'webm':
        return 'video/webm';
      case 'avi':
        return 'video/x-msvideo';
      case 'wmv':
        return 'video/x-ms-wmv';
      case 'flv':
        return 'video/x-flv';
      case 'mkv':
        return 'video/x-matroska';
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'ppt':
        return 'application/vnd.ms-powerpoint';
      case 'pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'txt':
        return 'text/plain';
      case 'zip':
        return 'application/zip';
      case 'rar':
        return 'application/x-rar-compressed';
      case 'mp3':
        return 'audio/mpeg';
      case 'wav':
        return 'audio/wav';
      case 'ogg':
        return 'audio/ogg';
      case 'svg':
        return 'image/svg+xml';
      case 'webp':
        return 'image/webp';
      case 'ico':
        return 'image/x-icon';
      case 'json':
        return 'application/json';
      case 'xml':
        return 'application/xml';
      case 'html':
        return 'text/html';
      case 'css':
        return 'text/css';
      case 'js':
        return 'application/javascript';
      default:
        return file.type || 'application/octet-stream';
    }
  }

  /**
   * Get presigned URL for file upload (with authentication)
   * @param {File} file - The file to get presigned URL for
   * @param {boolean} isProfile - Whether this is a profile upload
   * @returns {Promise<Object>} Response with presigned URL data
   */
  async getPresignedUrl(file, isProfile = false) {
    try {
      if (!file) {
        throw new Error('File is required');
      }
      
      const contentType = this.getContentType(file);
      
      const response = await apiClient.get(API_ENDPOINTS.GET_PRESIGNED_URL, {
        params: {
          filename: file.name,
          contentType: contentType,
          isProfile: isProfile
        }
      });

      if (!response.data?.data?.url) {
        throw new Error('No presigned URL received from server');
      }
      
      return ResponseHandler.handleSuccess(response, 'Presigned URL obtained successfully', response.data.data);
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to get presigned URL');
    }
  }

  /**
   * Get presigned URL for file upload (without authentication)
   * @param {File} file - The file to get presigned URL for
   * @param {boolean} isProfile - Whether this is a profile upload
   * @param {string} module - Module name for the upload
   * @returns {Promise<Object>} Response with presigned URL data
   */
  async getPresignedUrlWithoutAuth(file, isProfile = false, module) {
    try {
      if (!file) {
        throw new Error('File is required');
      }
      
      const contentType = this.getContentType(file);

      const response = await apiClient.get(API_ENDPOINTS.GET_PRESIGNED_URL_WITHOUT_AUTH, {
        params: {
          filename: file.name,
          contentType: contentType,
          isProfile: isProfile,
          module: module
        }
      });

      if (!response.data?.data?.url) {
        throw new Error('No presigned URL received from server');
      }
      
      return ResponseHandler.handleSuccess(response, 'Presigned URL obtained successfully', response.data.data);
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to get presigned URL');
    }
  }

  /**
   * Get signed URLs for a list of file keys
   * @param {string[]} fileKeys - Array of file keys to get signed URLs for
   * @param {boolean} isProfile - Whether these are profile files
   * @returns {Promise<Object>} Response with signed URLs data
   */
  async getSignedUrls(fileKeys, isProfile = false) {
    try {
      if (!fileKeys || !Array.isArray(fileKeys) || fileKeys.length === 0) {
        throw new Error('File keys array is required and must not be empty');
      }
      
      const response = await apiClient.post(API_ENDPOINTS.GET_SIGNED_URLS, {
        fileKeys,
        isProfile: isProfile
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.data?.data) {
        throw new Error('No signed URLs received from server');
      }

      return ResponseHandler.handleSuccess(response, 'Signed URLs obtained successfully', response.data.data);
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to get signed URLs');
    }
  }

  /**
   * Get presigned URLs for multipart upload
   * @param {string} contentType - The content type of the file
   * @param {number} fileSize - Size of the file in bytes
   * @param {number} partCount - Number of parts to split the file into
   * @returns {Promise<Object>} Response with multipart presigned URLs data
   */
  async getMultipartPresignedUrls(contentType, fileSize, partCount) {
    try {
      // Convert fileSize from bytes to MB
      const fileSizeInMB = fileSize / (1024 * 1024);

      const response = await apiClient.get(API_ENDPOINTS.GET_MULTIPART_PRESIGNED_URLS, {
        params: {
          contentType,
          fileSize: fileSizeInMB, // Send fileSize in MB
          partCount
        }
      });

      if (!response.data?.data) {
        throw new Error('No presigned URLs received from server');
      }

      return ResponseHandler.handleSuccess(response, 'Multipart presigned URLs obtained successfully', response.data.data);
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to get multipart presigned URLs');
    }
  }

  /**
   * Upload a file part to S3 via presigned URL (direct PUT, no app auth headers).
   * @param {string} url - Presigned URL for the part
   * @param {Blob} part - The file part to upload
   * @param {string} contentType - Content type of the file
   * @param {Object} [options] - Upload options forwarded to putBlobToPresignedUrl
   * @returns {Promise<Object>} Response with upload part data
   */
  async uploadPart(url, part, contentType, options = {}) {
    try {
      const { etag } = await putBlobToPresignedUrl(url, part, contentType, options);
      const partNumber = parseInt(new URL(url).searchParams.get('partNumber'), 10);

      if (!etag) {
        throw new Error('No ETag received from S3');
      }

      return {
        success: true,
        status: 200,
        message: 'File part uploaded successfully',
        data: {
          ETag: etag,
          PartNumber: partNumber,
        },
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: 200,
        },
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to upload file part');
    }
  }

  /**
   * Complete multipart upload
   * @param {string} key - The file key
   * @param {string} uploadId - The upload ID
   * @param {Array<{ETag: string, PartNumber: number}>} parts - Array of uploaded parts
   * @returns {Promise<Object>} Response with completion data
   */
  async completeMultipartUpload(key, uploadId, parts) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COMPLETE_MULTIPART_UPLOAD, {
        key,
        uploadId,
        parts
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: TIMEOUT_CONFIG.MULTIPART_UPLOAD
      });

      if (!response.data?.data) {
        throw new Error('Failed to complete multipart upload');
      }

      return ResponseHandler.handleSuccess(response, 'Multipart upload completed successfully', response.data.data);
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to complete multipart upload');
    }
  }
}

export default new FileService();
